import React from 'react'
import { addWeeks, subWeeks } from 'date-fns'
import { useWorkPeriodsQuery } from '../queries/work-periods'
import { WorkPeriod } from '../api/work-periods'
import { findNextWorkPeriodInList, findPreviousWorkPeriodInList } from '../utils/missions'
import { endOfWeek, startOfWeek } from '../utils/date'

/** Size of the nav range in weeks; aligned to week boundaries so same week = same query = cache hit. */
const NAV_RANGE_WEEKS = 1

/** Minimal work period shape needed to compute the nav date range. */
export interface WorkPeriodNavRangeInput {
  start: { date: Date }
  end: { date: Date }
}

/**
 * Fetches work periods for the mission in a date window around the current one
 * (so the backend returns virtuals) and returns previous/next for page navigation.
 * Range is aligned to week boundaries (Monday–Sunday) so work periods in the same
 * 2-week block share the same query params and React Query cache is reused.
 *
 * @param missionId - Mission ID (enables query when set).
 * @param workPeriod - Current work period or minimal { start, end } for range (enables query when set).
 * @param workPeriodId - Current work period ID to find prev/next in the list.
 * @returns { previousWorkPeriod, nextWorkPeriod }
 */
export function useWorkPeriodNavPrevNext(
  missionId: string | undefined,
  workPeriod: WorkPeriodNavRangeInput | null | undefined,
  workPeriodId: string | undefined,
): { previousWorkPeriod: WorkPeriod | undefined; nextWorkPeriod: WorkPeriod | undefined } {
  const navDateRange = React.useMemo(() => {
    if (!workPeriod) return undefined
    const date = new Date(workPeriod.start.date)
    const blockStart = startOfWeek(subWeeks(date, NAV_RANGE_WEEKS))
    const blockEnd = endOfWeek(addWeeks(date, NAV_RANGE_WEEKS))
    return { start: blockStart, end: blockEnd }
    // Dependency on block start time so same week = same range = same query key (cache hit)
  }, [workPeriod ? startOfWeek(new Date(workPeriod.start.date)).getTime() : undefined])

  const { data: workPeriodsForNav } = useWorkPeriodsQuery(
    missionId && navDateRange
      ? { mission: missionId, start: navDateRange.start, end: navDateRange.end }
      : undefined,
    { enabled: Boolean(missionId && navDateRange) },
  )

  return React.useMemo(() => {
    if (!workPeriodsForNav || !workPeriodId)
      return { previousWorkPeriod: undefined, nextWorkPeriod: undefined }
    return {
      previousWorkPeriod: findPreviousWorkPeriodInList(workPeriodsForNav, workPeriodId),
      nextWorkPeriod: findNextWorkPeriodInList(workPeriodsForNav, workPeriodId),
    }
  }, [workPeriodsForNav, workPeriodId])
}
